/**
 * Pause state machine. The game starts paused behind the start overlay; `start()` (the "Click to fly"
 * gesture) emits `firstGesture` once and resumes. After that, `pause(reason)` stops the game once and
 * announces it on the bus (the menu feature shows its overlay); `bus 'resume'` resumes it. The hooks do
 * the DOM work (pointer lock, stepper reset) so this stays testable.
 */
import type { GameBus, PauseReason } from './feature'

export interface PauseHooks {
  onPause(): void
  /** Runs synchronously inside the gesture that resumed (pointer lock needs that). */
  onResume(): void
}

export interface PauseControl {
  readonly paused: boolean
  /** The first user gesture: emits `firstGesture` once, then resumes. */
  start(): void
  pause(reason: PauseReason): void
}

export function createPauseControl(bus: GameBus, hooks: PauseHooks): PauseControl {
  let paused = true
  let started = false
  const resume = (): void => {
    if (!paused) return
    paused = false
    hooks.onResume()
  }
  bus.on('resume', () => {
    if (started) resume()
  })
  return {
    get paused(): boolean {
      return paused
    },
    start(): void {
      if (!started) {
        started = true
        bus.emit('firstGesture', undefined)
      }
      resume()
    },
    pause(reason: PauseReason): void {
      if (paused || !started) return
      paused = true
      hooks.onPause()
      bus.emit('pause', reason)
    },
  }
}
